import { ColDef } from "ag-grid-community";

import type { ProjectPage } from "bundles/projects/api/ProjectPagesApi";
import { extractFilename } from "bundles/projects/components/takeoffs/PdfManager/utils/filenameUtils";
import type { PdfFileItem } from "bundles/projects/types/takeoff/pdfManager";
import { inferTradeFromPageCode } from "bundles/projects/utils/takeoff/tradeMapping";
import {
  isElectricalTradeData,
  type TakeoffHandler,
  type TakeoffRecord,
  type TakeoffGenerationFileDetail,
  type TakeoffGenerationsFilesDetail,
  type Feeder,
  type TakeoffGenerationFileJsonData,
  type UnifiedMeasurementData,
  type UnifiedResult,
} from "bundles/projects/types";
import type { TradeKey } from "bundles/projects/consts/tradeScopes";

export interface PopupPosition {
  x: number;
  y: number;
}

/**
 * Prefix added by backend to cancelled job IDs.
 * When user clicks "Stop", the backend prefixes page job_ids with this
 * to prevent frontend from polling construction-platform on page refresh.
 */
export const CANCELLED_JOB_PREFIX = "cancelled-";

/**
 * Checks if a job ID represents a cancelled job.
 * Cancelled jobs have their job_id prefixed with "cancelled-" by the backend
 * to prevent the frontend from polling construction-platform on page refresh.
 *
 * @param jobId - The job ID to check
 * @returns true if the job ID starts with "cancelled-"
 */
export const isCancelledJobId = (jobId: string | null | undefined): boolean => {
  if (!jobId) return false;
  return jobId.startsWith(CANCELLED_JOB_PREFIX);
};

/**
 * Whether a job id is a valid target to append/re-run takeoff against.
 * False for a missing id (start a fresh job) and false for a `cancelled-`
 * sentinel — a cancelled job is dead server-side, so appending to it would
 * hit `api/v4/takeoff/cancelled-<uuid>/process` and 404 (ENG-12804). Callers
 * use this to decide append-vs-fresh; a cancelled job must start fresh.
 *
 * @param jobId - The current job ID (may carry the "cancelled-" prefix)
 * @returns true only for a present, non-cancelled job id
 */
export const canAppendToJob = (jobId: string | null | undefined): boolean => {
  return !!jobId && !isCancelledJobId(jobId);
};

/**
 * Checks if a job should skip processing (cancelled or no active job).
 * Use this for guards in URL refresh, JSON fetch, and polling logic.
 *
 * @param pageJobId - The job ID from the page (may have "cancelled-" prefix)
 * @param activeJobId - The current active job ID from context (null if no active job)
 * @returns true if processing should be skipped
 */
export const shouldSkipJobProcessing = (
  pageJobId: string | null | undefined,
  activeJobId: string | null | undefined
): boolean => {
  return isCancelledJobId(pageJobId) || !activeJobId;
};

/**
 * Calculates the position for a popup relative to a button click event
 */
export const calculatePopupPosition = (
  event: React.MouseEvent,
  offsetX: number = 10,
  offsetY: number = 0,
): PopupPosition | null => {
  const rect = event.currentTarget.getBoundingClientRect();
  const containerRect = event.currentTarget
    .closest(".ag-theme-alpine")
    ?.getBoundingClientRect();

  if (!containerRect) return null;

  // Position popup to the right of the button, relative to the container
  // Account for the container's padding-left (pl-8 = 32px)
  return {
    x: rect.right - containerRect.left + offsetX,
    y: rect.top - containerRect.top + offsetY,
  };
};

/**
 * Gets the position and style for a floating circle button for AG Grid rows
 */
export const getFloatingButtonPosition = (node: any) => {
  const rowElement = document.querySelector(`[row-id="${node.id}"]`);
  if (!rowElement) return null;

  const rect = rowElement.getBoundingClientRect();
  const containerRect = rowElement
    .closest(".ag-theme-alpine")
    ?.getBoundingClientRect();

  if (!containerRect) return null;

  return {
    top: rect.top - containerRect.top + rect.height / 2 - 10,
    left: 0,
    zIndex: 10,
  };
};

/**
 * Prepares column definitions for AG Grid with common settings
 */
export const prepareColumnDefsForGrid = (csvColumnDefs: ColDef[]): ColDef[] => {
  return csvColumnDefs.map((colDef) => ({
    ...colDef,
    suppressHeaderMenuButton: true,
    menuTabs: undefined,
    flex: 1, // Auto-fit columns to available space
    minWidth: 100, // Minimum width to prevent columns from being too narrow
  }));
};

/**
 * Parses the Description field to extract from and to values
 * Example: "Util XFM to MDP" -> ["Util XFM", "MDP"]
 * Handles edge cases: multiple " to ", empty parts, case variations
 */
export const parseDescriptionField = (
  description: string,
): [string, string] | null => {
  if (!description || typeof description !== "string") {
    return null;
  }

  // Handle case-insensitive " to " splitting (including " TO ", " To ", etc.)
  const lowerDescription = description.toLowerCase();
  const toIndex = lowerDescription.indexOf(" to ");

  if (toIndex === -1) {
    return null;
  }

  const fromPart = description.substring(0, toIndex).trim();
  const toPart = description.substring(toIndex + 4).trim(); // +4 for " to "

  // Ensure both parts are non-empty
  if (!fromPart || !toPart) {
    return null;
  }

  return [fromPart, toPart];
};

/**
 * Normalizes measurement source by replacing spaces with underscores
 * @param source - The source string to normalize
 * @param keepExtension - Whether to keep the file extension (default: false)
 */
export const normalizeMeasurementSource = (
  source: string,
  keepExtension: boolean = false,
): string => {
  if (keepExtension) {
    return source.replace(/\s+/g, "_");
  }
  return source.replace(/\.pdf$/i, "").replace(/\s+/g, "_");
};

/**
 * Gets all possible filename variations for measurement storage compatibility
 * Handles migration from old format ("P5 - Page 5.pdf") to new format ("red-cedar-p5.pdf")
 * @param currentFileName - The current filename (could be old or new format)
 * @param pageData - Optional page data to generate alternative filename formats
 * @returns Array of possible filename variations to check in storage
 */
export const getMeasurementStorageKeys = (
  currentFileName: string,
  pageData?: { code: string; title: string; source_pdf: string },
): string[] => {
  const variations: string[] = [currentFileName];

  // Always include the normalized version
  const normalized = normalizeMeasurementSource(currentFileName, true);
  if (normalized !== currentFileName) {
    variations.push(normalized);
  }

  if (pageData) {
    // Generate old format: "P5 - Page 5.pdf"
    const oldFormat = `${pageData.code} - ${pageData.title}.pdf`;
    const oldFormatNormalized = normalizeMeasurementSource(oldFormat, true);

    // Add all variations
    [oldFormat, oldFormatNormalized, pageData.source_pdf].forEach((variant) => {
      if (!variations.includes(variant)) {
        variations.push(variant);
      }
    });
  }

  return variations;
};

/**
 * Normalizes a string for use as a filename by removing/replacing invalid characters
 */
export const normalizeFilename = (filename: string): string => {
  return filename
    .replace(/[<>:"/\\|?*]/g, "") // Remove invalid filename characters
    .replace(/\s+/g, "_") // Replace spaces with underscores
    .replace(/_{2,}/g, "_") // Replace multiple underscores with single underscore
    .replace(/^_+|_+$/g, "") // Remove leading/trailing underscores
    .substring(0, 100); // Limit length to 100 characters
};

/**
 * Checks if a filename represents an enlarged plan drawing
 * @param filename - The filename to check
 * @returns true if the file contains "enlarged"
 */
export const isEnlargedPlanFile = (filename: string): boolean => {
  if (!filename) return false;
  const upperFilename = filename.toUpperCase();
  return upperFilename.includes("ENLARGED");
};

/**
 * Exports AG Grid data as CSV file with aggregated beam package format
 * Format: Trade (empty), Scope (count), Group (beam name), Quantities (feet-inch)
 */
export const exportGridDataAsCSV = (
  rowData: any[],
  columnDefs: ColDef[],
  filename: string,
): void => {
  if (!rowData.length || !columnDefs.length) return;

  // Helper: Convert decimal feet to feet-inch format
  const formatFeetInches = (feet: number): string => {
    let wholeFeet = Math.floor(feet);
    let inches = Math.round((feet - wholeFeet) * 12);

    // Handle rollover when inches rounds to 12
    if (inches === 12) {
      wholeFeet += 1;
      inches = 0;
    }

    return `${wholeFeet}'- ${inches}`;
  };

  // Aggregate measurements by Group + Length (rounded to nearest inch)
  const aggregationMap = new Map<
    string,
    { group: string; lengthFeet: number; count: number }
  >();

  rowData.forEach((row) => {
    const group = row.Group;
    const qtyValue = row["Qty 1"];

    // Skip rows without group or invalid quantity
    if (!group || !qtyValue || qtyValue <= 0) return;

    // Round length to nearest inch for grouping
    const roundedLength = Math.round(qtyValue * 12) / 12;
    const key = `${group}|${roundedLength}`;

    if (aggregationMap.has(key)) {
      aggregationMap.get(key)!.count++;
    } else {
      aggregationMap.set(key, { group, lengthFeet: roundedLength, count: 1 });
    }
  });

  // Convert to array and sort
  const aggregatedData = Array.from(aggregationMap.values()).sort((a, b) => {
    // Primary: group name (alphabetical)
    if (a.group !== b.group) return a.group.localeCompare(b.group);
    // Secondary: count (descending)
    if (a.count !== b.count) return b.count - a.count;
    // Tertiary: length (ascending)
    return a.lengthFeet - b.lengthFeet;
  });

  // Build CSV with new format
  const headers = ["Piece Mark", "Qty", "Type", "Length"];
  const csvContent = [
    headers.join(","),
    ...aggregatedData.map((item) => {
      return [
        "", // Piece Mark column empty
        item.count, // Qty = count
        item.group, // Type = beam name
        formatFeetInches(item.lengthFeet), // Length = feet-inch format
      ].join(",");
    }),
  ].join("\n");

  // Create and trigger download
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);

  link.setAttribute("href", url);
  link.setAttribute("download", filename);
  link.style.visibility = "hidden";

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  URL.revokeObjectURL(url);
};

/**
 * Finds matching feeder data in JSON based on from, to, and measurement_source
 */
export const findMatchingFeederData = (
  jsonData: TakeoffGenerationFileJsonData,
  from: string,
  to: string,
  measurementSource: string,
  currentPageName?: string,
): Feeder[] => {
  // Only work with electrical trade data that has feeders
  if (
    !jsonData ||
    !isElectricalTradeData(jsonData) ||
    !jsonData.feeders ||
    !Array.isArray(jsonData.feeders)
  ) {
    return [];
  }

  // Normalize the measurement source from CSV (remove .pdf if present)
  const normalizedCsvSource = normalizeMeasurementSource(measurementSource);

  return jsonData.feeders.filter((feeder: Feeder) => {
    // TODO [Rezha]: this is a temporary patch for demo, fix later after BE is ready
    // Normalize the measurement source from JSON (remove .pdf if present)
    const defaultSource = currentPageName || "ENLARGED";
    const normalizedJsonSource = feeder.measurement_source
      ? normalizeMeasurementSource(feeder.measurement_source)
      : normalizeMeasurementSource(defaultSource);

    // Handle both old and new property names for feeder equipment
    // Add explicit fallbacks to prevent undefined segments in keys
    const feederFrom = feeder.from || feeder.source_equipment || "UNKNOWN_FROM";
    const feederTo = feeder.to || feeder.destination_equipment || "UNKNOWN_TO";

    // For electrical feeders created from the same data, the sources should match exactly
    // Use exact equality for current page sources, fallback to startsWith for legacy data
    const sourceMatch =
      normalizedJsonSource === normalizedCsvSource ||
      normalizedJsonSource.startsWith(normalizedCsvSource);

    // Ensure all values are defined before matching to prevent undefined === undefined matches
    const result =
      feederFrom &&
      feederTo &&
      from &&
      to &&
      feederFrom === from &&
      feederTo === to &&
      sourceMatch;

    return result;
  });
};

/**
 * Gets source references for a selected row
 */
export const getSourceReferences = (
  selectedRowData: any,
  jsonData: TakeoffGenerationFileJsonData | null,
  currentPageName?: string,
): Feeder[] => {
  if (!selectedRowData || !jsonData) {
    return [];
  }

  // Step 1: Get Description field value
  const description = selectedRowData.Description;
  if (!description) {
    return [];
  }

  // Step 2: Parse description to get from/to values
  const fromToArray = parseDescriptionField(description);
  if (!fromToArray) {
    return [];
  }

  const [from, to] = fromToArray;

  // Step 3: Get Drawing field (measurement_source)
  const measurementSource = selectedRowData.Drawing;
  if (!measurementSource) {
    return [];
  }

  // Step 4: Find matching feeder data
  const matchingFeeders = findMatchingFeederData(
    jsonData,
    from,
    to,
    measurementSource,
    currentPageName,
  );

  return matchingFeeders;
};

/**
 * Finds a matching file in the files list by measurement_source
 * Maps from PDF filename in JSON data to unique page code
 */
export const findMatchingFileByMeasurementSource = (
  measurementSource: string,
  filesList: TakeoffGenerationsFilesDetail,
): TakeoffGenerationFileDetail | null => {
  if (!measurementSource || !filesList || filesList.length === 0) {
    return null;
  }

  // Normalize the measurement source (remove trailing .pdf and replace spaces with underscores)
  // e.g., "Red Cedar - Brooks Floor Plans.pdf" -> "Red_Cedar_-_Brooks_Floor_Plans"
  const normalizedMeasurementSource =
    normalizeMeasurementSource(measurementSource);

  // Find a page that matches by sourcePdf field, but return the page identified by its unique code
  const matchingPage = filesList.find((file) => {
    // file.fileName now contains unique page code (e.g., "S2.3")
    // file.sourcePdf contains the source PDF filename (e.g., "red-cedar-p5.pdf")

    if (!file.fileUrl) {
      return false;
    }

    const normalizedSourcePdf = normalizeMeasurementSource(file.fileName);

    // Try both startsWith (prefix) and exact matching for the base filename
    const prefixMatch =
      normalizedMeasurementSource.startsWith(normalizedSourcePdf);
    const exactMatch = normalizedMeasurementSource === normalizedSourcePdf;
    const reverseMatch = normalizedSourcePdf.startsWith(
      normalizedMeasurementSource,
    );

    const isMatch = prefixMatch || exactMatch || reverseMatch;

    return isMatch;
  });

  return matchingPage || null;
};

// Removed createFeederLookupMap - no longer needed with exact matching approach

/**
 * Validates measurement change parameters for drag-and-drop operations
 */
export const validateMeasurementParams = (
  feeder: Feeder,
  newLength: number,
  oldLength: number,
  hasJsonData: boolean,
): { isValid: boolean; error?: string } => {
  if (!hasJsonData) {
    return { isValid: false, error: "No JSON data available" };
  }

  if (!feeder) {
    return { isValid: false, error: "No feeder provided" };
  }

  if (oldLength <= 0) {
    return { isValid: false, error: "Invalid old length" };
  }

  if (newLength <= 0) {
    return { isValid: false, error: "Invalid new length" };
  }

  return { isValid: true };
};

/**
 * Calculates and validates the measurement ratio for proportional updates
 */
export const calculateMeasurementRatio = (
  newLength: number,
  oldLength: number,
): { ratio: number; isValid: boolean; error?: string } => {
  const ratio = newLength / oldLength;

  if (!isFinite(ratio) || ratio <= 0) {
    return {
      ratio: 0,
      isValid: false,
      error: `Invalid ratio calculated: ${ratio}`,
    };
  }

  return { ratio, isValid: true };
};

/**
 * Updates feeder data with new measurements using exact matching
 * Replaces index-based updates to maintain consistency with CSV updates
 */
export const updateFeedersWithMeasurements = (
  feeders: Feeder[],
  targetFeeder: Feeder,
  originalMeasurement: {
    length: number;
    conduit_length_ft?: number;
    wire_length_ft?: number;
  },
  ratio: number,
  currentPageName?: string,
): Feeder[] => {
  return feeders.map((feeder) => {
    // Handle both old and new property names for feeder equipment
    // Add explicit fallbacks to prevent undefined segments in keys
    const feederFrom = feeder.from || feeder.source_equipment || "UNKNOWN_FROM";
    const feederTo = feeder.to || feeder.destination_equipment || "UNKNOWN_TO";
    const targetFrom =
      targetFeeder.from || targetFeeder.source_equipment || "UNKNOWN_FROM";
    const targetTo =
      targetFeeder.to || targetFeeder.destination_equipment || "UNKNOWN_TO";

    // Use exact matching logic (same as CSV updates)
    const defaultSource = currentPageName || "ENLARGED";

    // Ensure both from/to values are defined before matching to prevent undefined === undefined matches
    if (
      feederFrom &&
      feederTo &&
      targetFrom &&
      targetTo &&
      feederFrom.trim().toLowerCase() === targetFrom.trim().toLowerCase() &&
      feederTo.trim().toLowerCase() === targetTo.trim().toLowerCase() &&
      (feeder.measurement_source || defaultSource) ===
      (targetFeeder.measurement_source || defaultSource)
    ) {
      return {
        ...feeder,
        length: originalMeasurement.length * ratio,
        conduit_length_ft:
          originalMeasurement.conduit_length_ft !== undefined
            ? originalMeasurement.conduit_length_ft * ratio
            : feeder.conduit_length_ft,
        wire_length_ft:
          originalMeasurement.wire_length_ft !== undefined
            ? originalMeasurement.wire_length_ft * ratio
            : feeder.wire_length_ft,
      };
    }
    return feeder;
  });
};

// Removed updateCSVFormatWithMeasurements - replaced by updateCSVFormatByMatch for consistency

/**
 * Simple, exact matching for feeders and CSV records
 * Replaces loose includes() logic to prevent false positives
 */
export const findMatchingCSVRecord = (
  csvRecords: any[],
  feeder: Feeder,
): { record: any; index: number } | null => {
  for (let i = 0; i < csvRecords.length; i++) {
    const record = csvRecords[i];
    if (!record.Description) continue;

    // Parse description and do exact matching
    const parsedDescription = parseDescriptionField(record.Description);
    if (parsedDescription) {
      const [csvFrom, csvTo] = parsedDescription;

      // Handle both old and new property names for feeder equipment
      // Add explicit fallbacks to prevent undefined segments in keys
      const feederFrom =
        feeder.from || feeder.source_equipment || "UNKNOWN_FROM";
      const feederTo =
        feeder.to || feeder.destination_equipment || "UNKNOWN_TO";

      // Exact match (case insensitive, trimmed)
      if (
        feederFrom &&
        feederTo &&
        csvFrom.trim().toLowerCase() === feederFrom.trim().toLowerCase() &&
        csvTo.trim().toLowerCase() === feederTo.trim().toLowerCase()
      ) {
        return { record, index: i };
      }
    }
  }

  return null;
};

/**
 * Updates CSV format records with new measurements by finding matching records
 * Uses exact matching instead of loose includes() to prevent data inconsistency
 */
export const updateCSVFormatByMatch = (
  records: any[],
  feeder: Feeder,
  originalMeasure: number,
  ratio: number,
): any[] => {
  const matchResult = findMatchingCSVRecord(records, feeder);

  if (!matchResult) {
    // Handle both old and new property names for feeder equipment
    // Add explicit fallbacks to prevent undefined segments in keys
    const feederFrom = feeder.from || feeder.source_equipment || "UNKNOWN_FROM";
    const feederTo = feeder.to || feeder.destination_equipment || "UNKNOWN_TO";
    console.warn(
      `No matching CSV record found for feeder: ${feederFrom} -> ${feederTo}`,
    );
    return records;
  }

  return records.map((record, index) => {
    if (index === matchResult.index) {
      return {
        ...record,
        Measure: originalMeasure * ratio,
      };
    }
    return record;
  });
};

/**
 * Normalizes a category/scope name for comparison by removing hyphens, underscores, and spaces.
 * This handles different naming conventions:
 * - "concrete-slabs" (hyphen) -> "concreteslabs"
 * - "Concrete Slabs" (space) -> "concreteslabs"
 * - "concrete_slabs" (underscore) -> "concreteslabs"
 *
 * NOTE: This function is duplicated near replaceScopeInUnifiedResults for locality.
 * If you update one, update the other. See CLAUDE.md for details.
 */
const normalizeCategoryName = (name: string | null | undefined): string =>
  (name ?? "").toLowerCase().replace(/[-_\s]/g, "");

/**
 * Checks if categories match by (name, trade).
 * For unmigrated data where trade is undefined, matches on name only.
 * Uses normalized comparison to handle different naming conventions.
 *
 * @param existingCategory - The existing category (may have undefined trade)
 * @param newCategoryName - The new category name to match
 * @param newCategoryTrade - The new category trade to match
 * @returns True if categories match (by name+trade or name-only for unmigrated data)
 */
const categoriesMatchByNameAndTrade = (
  existingCategory: { name: string; trade?: TradeKey },
  newCategoryName: string,
  newCategoryTrade: TradeKey,
): boolean => {
  // Name must match (normalized to handle different naming conventions)
  if (normalizeCategoryName(existingCategory.name) !== normalizeCategoryName(newCategoryName)) {
    return false;
  }

  // If existing trade is undefined (unmigrated data), match by name only
  if (existingCategory.trade === undefined) return true;

  // Both have trade defined - must match exactly
  return existingCategory.trade === newCategoryTrade;
};

/**
 * Migrates the trade field for unmigrated category data.
 * Should be called after a successful match when existingCategory.trade is undefined.
 *
 * @param existingCategory - The existing category to migrate
 * @param newCategoryTrade - The trade value to set
 */
const migrateCategoryTrade = (
  existingCategory: { name: string; trade?: TradeKey },
  newCategoryTrade: TradeKey,
): void => {
  if (existingCategory.trade === undefined) {
    existingCategory.trade = newCategoryTrade;
  }
};

/**
 * Smart merge of unified measurement data results
 * For matching category-group pairs, removes backend-sourced items from existing data
 * and replaces them with new items from the incoming data.
 *
 * This is useful when re-running takeoff intelligence on the same scope - it removes
 * old AI-generated measurements and replaces them with fresh ones, while preserving
 * user-drawn or manually edited measurements.
 *
 * @param existingData - The existing unified measurement data
 * @param newData - The new unified measurement data to merge in
 * @returns Merged unified measurement data
 */
export const smartMergeUnifiedResults = (
  existingData: UnifiedMeasurementData,
  newData: UnifiedMeasurementData,
): UnifiedMeasurementData => {
  // Deep clone existing results to avoid mutation
  const mergedResults = JSON.parse(
    JSON.stringify(existingData.result),
  ) as typeof existingData.result;

  // Track which newResults have been entirely added to avoid re-processing their categories
  const addedNewResults = new Set<any>();

  // Process each new result
  for (const newResult of newData.result) {
    if (!newResult.json.categories) continue;

    // Skip if this entire result was already added
    if (addedNewResults.has(newResult)) {
      console.log("⏭️ Skipping newResult that was already added entirely");
      continue;
    }

    // For each category in new data
    for (const newCategory of newResult.json.categories) {
      const categoryName = newCategory.name;

      // Track which groups have been processed for this category
      const processedGroups = new Set<string>();

      // For each group in new category
      for (const newGroup of newCategory.groups) {
        const groupName = newGroup.name;

        // Skip if already processed
        if (processedGroups.has(groupName)) continue;
        processedGroups.add(groupName);

        // Find matching category-group in existing results
        let foundCategoryGroupMatch = false;
        let foundCategoryOnly = false;
        let targetCategoryForNewGroup: any = null;
        let targetResultForNewCategory: any = null;

        for (const existingResult of mergedResults) {
          if (!existingResult.json.categories) continue;

          for (const existingCategory of existingResult.json.categories) {
            if (!categoriesMatchByNameAndTrade(existingCategory, categoryName, newCategory.trade)) continue;

            // Found matching category - migrate trade if needed for unmigrated data
            migrateCategoryTrade(existingCategory, newCategory.trade);
            foundCategoryOnly = true;
            targetResultForNewCategory = existingResult;

            // Try to find matching group within this category
            for (const existingGroup of existingCategory.groups) {
              if (existingGroup.name !== groupName) continue;

              // Found matching category-group
              foundCategoryGroupMatch = true;

              // Remove items with source === "backend"
              existingGroup.items = existingGroup.items.filter(
                (item) => item.metadata?.source !== "backend",
              );

              // Renumber remaining user items starting from 1
              existingGroup.items.forEach((item, index) => {
                item.name = `${existingGroup.name} (${index + 1})`;
              });

              // Add new items with sequential numbering after user items
              const startNumber = existingGroup.items.length + 1;
              newGroup.items.forEach((newItem, index) => {
                newItem.name = `${existingGroup.name} (${startNumber + index})`;
              });

              existingGroup.items.push(...newGroup.items);
              break;
            }

            // If category exists but group doesn't, save reference to add group later
            if (!foundCategoryGroupMatch && !targetCategoryForNewGroup) {
              targetCategoryForNewGroup = existingCategory;
            }

            // Stop searching once we found the matching category
            if (foundCategoryOnly) break;
          }

          // Stop searching results once we found the matching category
          if (foundCategoryOnly) break;
        }

        // If we found the category but not the group, add the new group to that category
        if (
          foundCategoryOnly &&
          !foundCategoryGroupMatch &&
          targetCategoryForNewGroup
        ) {
          targetCategoryForNewGroup.groups.push(newGroup);
        }

        // If no category match found at all, we need to add the entire category once
        if (!foundCategoryOnly && !foundCategoryGroupMatch) {
          // Try to find result with same pdf/output_path/pageId
          // In aggregated mode (BulkTakeoffsTab), each result has a pageId field added
          let targetResult = mergedResults.find(
            (r) =>
              r.pdf === newResult.pdf ||
              r.output_path === newResult.output_path ||
              ((r as any).pageId &&
                (r as any).pageId === (newResult as any).pageId),
          );

          // Debug logging for troubleshooting
          if (!targetResult && mergedResults.length > 0) {
            console.warn(
              `⚠️ smartMerge: No matching result found for category "${categoryName}"`,
              {
                newResult: {
                  pdf: newResult.pdf,
                  output_path: newResult.output_path,
                  pageId: (newResult as any).pageId,
                },
                existingResults: mergedResults.map((r) => ({
                  pdf: r.pdf,
                  output_path: r.output_path,
                  pageId: (r as any).pageId,
                })),
              },
            );
          }

          if (!targetResult) {
            // No matching result, add the entire new result (with all its categories)
            mergedResults.push(newResult);
            // Mark this newResult as entirely added to skip processing its remaining categories
            addedNewResults.add(newResult);
            // We need to break out of BOTH loops (group and category) to move to next result
            // Setting a flag and breaking will exit the group loop, then the category loop will check the flag
            break;
          } else {
            // Found matching result, add the entire category with all its groups (only once)
            if (!targetResult.json.categories) {
              targetResult.json.categories = [];
            }
            // Check if category already exists (shouldn't, but safeguard)
            // Use helper for runtime migration of unmigrated data (trade === undefined)
            const categoryExists = targetResult.json.categories.some((cat: any) =>
              categoriesMatchByNameAndTrade(cat, categoryName, newCategory.trade),
            );
            if (!categoryExists) {
              targetResult.json.categories.push(newCategory);
            }
            // Mark all groups in this category as processed to avoid re-adding
            newCategory.groups.forEach((g) => processedGroups.add(g.name));
            break; // Move to next category
          }
        }
      }

      // Check if this result was marked as entirely added - if so, break out of category loop
      if (addedNewResults.has(newResult)) {
        break;
      }
    }
  }

  return {
    ...existingData,
    result: mergedResults,
    // ✅ Override takeoff_page_id with the NEW data's pageId
    // This ensures the guard in TakeoffsContext works correctly when detecting page mismatches
    takeoff_page_id: newData.takeoff_page_id,
  };
};

/**
 * Replace a scope's data in unified results for atomic sync.
 *
 * Unlike smartMergeUnifiedResults which tries to merge by category/group,
 * this function completely replaces all data for a given scope with new data.
 * This mirrors the backend's atomic scope replacement behavior.
 *
 * The function:
 * 1. Removes all categories matching the scope (case-insensitive)
 * 2. Adds new categories from the incoming data
 * 3. Preserves categories from other scopes
 *
 * @param existingData - The existing unified measurement data
 * @param newData - The new unified measurement data to replace with
 * @param scope - The scope being replaced (e.g., "walls", "feeders")
 * @returns Updated unified measurement data with scope replaced
 */
/**
 * Normalizes a scope/category name for comparison by removing hyphens, underscores, and spaces.
 * This handles different naming conventions:
 * - "concrete-slabs" (hyphen) -> "concreteslabs"
 * - "Concrete Slabs" (space) -> "concreteslabs"
 * - "concrete_slabs" (underscore) -> "concreteslabs"
 *
 * NOTE: This function is duplicated as normalizeCategoryName near smartMergeUnifiedResults.
 * If you update one, update the other. See CLAUDE.md for details.
 */
export const normalizeScopeName = (name: string | null | undefined): string =>
  (name ?? "").toLowerCase().replace(/[-_\s]/g, "");

/**
 * Checks if an item is user-modified and should be preserved during scope replacement.
 * User-modified items have modified_by_user=true or non-AI sources.
 *
 * When the scope being replaced is any home-run-routing variant, user_drawn
 * homerun items are NOT preserved — the backend routing result is authoritative
 * and replaces manual placements.
 *
 * Variants covered (all share the "home-run-routing" prefix):
 *   - home-run-routing                   (normal)
 *   - home-run-routing-takeoff           (normal beta — Circuit Word Modal)
 *   - home-run-routing-jbox              (JBox suggestions)
 *   - home-run-routing-jbox-takeoff      (JBox beta — Circuit Word Modal)
 */
const isHomeRunRoutingScope = (scope?: string): boolean =>
  !!scope && scope.startsWith("home-run-routing");

const HOME_RUN_CATEGORY_ROOTS = [
  "Panels",
  "Home Runs",
  "Branches",
  "(Unclassified)",
] as const;

const isUserModifiedItem = (item: any, scope?: string): boolean => {
  if (!item?.metadata) return false;
  // When re-routing home runs, drop user_drawn homerun items so the backend
  // routing result takes full ownership of the home-run-routing scope.
  // Covers all 4 home-run-routing variants (normal, normal beta, jbox, jbox beta).
  if (
    isHomeRunRoutingScope(scope) &&
    item.metadata.trade_scope === "home-run-routing" &&
    item.metadata.source === "user_drawn"
  ) {
    return false;
  }
  // Explicitly marked as modified by user
  if (item.metadata.modified_by_user === true) return true;
  // Non-AI sources should be preserved (user_drawn, manual, edited_backend)
  const preservedSources = ["user_drawn", "manual", "edited_backend"];
  if (item.metadata.source && preservedSources.includes(item.metadata.source)) {
    return true;
  }
  return false;
};

export const replaceScopeInUnifiedResults = (
  existingData: UnifiedMeasurementData,
  newData: UnifiedMeasurementData,
  scope: string,
): UnifiedMeasurementData => {
  // Deep clone existing results to avoid mutation
  const mergedResults = JSON.parse(
    JSON.stringify(existingData.result),
  ) as typeof existingData.result;

  // Collect all category names from the new data.
  // Some scopes (like steel-beams-joists) use sub-categories (joist, w_small, w_medium, etc.)
  // instead of the top-level scope name. We need to remove/replace ALL these categories.
  const newCategoryNames = new Set<string>();
  for (const newResult of newData.result) {
    if (!newResult.json?.categories) continue;
    for (const category of newResult.json.categories) {
      newCategoryNames.add(normalizeScopeName(category.name));
    }
  }

  // Also include the scope itself for backwards compatibility
  const normalizedScope = normalizeScopeName(scope);
  newCategoryNames.add(normalizedScope);
  if (isHomeRunRoutingScope(scope)) {
    HOME_RUN_CATEGORY_ROOTS.forEach((name) =>
      newCategoryNames.add(normalizeScopeName(name)),
    );
  }

  // Extract user-modified items from categories that will be replaced
  // Key: "normalizedCategoryName|groupName" -> items[]
  const preservedUserItems = new Map<string, any[]>();

  for (const existingResult of mergedResults) {
    if (!existingResult.json?.categories) continue;

    for (const category of existingResult.json.categories) {
      const normalizedCatName = normalizeScopeName(category.name);
      // Only extract from categories that will be replaced
      if (!newCategoryNames.has(normalizedCatName)) continue;

      for (const group of category.groups || []) {
        const userItems = (group.items || []).filter((item) => isUserModifiedItem(item, scope));
        if (userItems.length > 0) {
          const key = `${normalizedCatName}|${group.name}`;
          const existing = preservedUserItems.get(key) || [];
          preservedUserItems.set(key, [...existing, ...userItems]);
        }
      }
    }
  }

  // Remove categories matching any of the new category names from existing results
  for (const existingResult of mergedResults) {
    if (!existingResult.json?.categories) continue;

    existingResult.json.categories = existingResult.json.categories.filter(
      (cat) => !newCategoryNames.has(normalizeScopeName(cat.name)),
    );
  }

  // Add ALL new categories from the new data (not filtered by scope)
  for (const newResult of newData.result) {
    if (!newResult.json?.categories) continue;

    for (const newCategory of newResult.json.categories) {
      // Find a target result to add this category to
      // Try to find result with same pdf/output_path/pageId
      let targetResult = mergedResults.find(
        (r) =>
          r.pdf === newResult.pdf ||
          r.output_path === newResult.output_path ||
          ((r as any).pageId &&
            (r as any).pageId === (newResult as any).pageId),
      );

      if (!targetResult) {
        // No matching result, use the first one or create a new entry
        if (mergedResults.length > 0) {
          targetResult = mergedResults[0];
        } else {
          // Create a minimal result entry
          targetResult = {
            pdf: newResult.pdf,
            json: { categories: [] },
          };
          mergedResults.push(targetResult);
        }
      }

      if (!targetResult.json.categories) {
        targetResult.json.categories = [];
      }

      // Merge preserved user-modified items back into matching groups
      const normalizedCatName = normalizeScopeName(newCategory.name);
      for (const group of newCategory.groups || []) {
        const key = `${normalizedCatName}|${group.name}`;
        const userItems = preservedUserItems.get(key);
        if (userItems && userItems.length > 0) {
          // Deduplicate by ID to prevent duplicate items when same ID exists
          // in both newData and userItems (e.g., user edits during bulk takeoff)

          // Create a map of user items by ID for O(1) lookup
          const userItemsById = new Map<string, any>();
          for (const userItem of userItems) {
            if (userItem.id) {
              userItemsById.set(userItem.id, userItem);
            }
          }

          // Merge items: for each new item, check if there's a user-modified version
          const mergedItems: any[] = [];
          const processedUserIds = new Set<string>();

          for (const newItem of group.items || []) {
            const userItem = newItem.id ? userItemsById.get(newItem.id) : null;
            if (userItem) {
              // Same ID exists in user items - merge user data with new data
              // User data takes precedence (preserves position, qty, etc.)
              // But we want annotation_id and other sync metadata from new item
              const mergedItem = {
                ...newItem,
                ...userItem,
                metadata: {
                  ...newItem.metadata,
                  ...userItem.metadata,
                  // Keep annotation_id from new data if user item doesn't have one
                  annotation_id: userItem.metadata?.annotation_id ?? newItem.metadata?.annotation_id,
                },
              };
              // Ensure qty is valid - prefer user's qty, fallback to new item's qty, then 0
              if (typeof mergedItem.qty !== 'number' || isNaN(mergedItem.qty)) {
                mergedItem.qty = (typeof userItem.qty === 'number' && !isNaN(userItem.qty))
                  ? userItem.qty
                  : (typeof newItem.qty === 'number' && !isNaN(newItem.qty))
                    ? newItem.qty
                    : 0;
              }
              mergedItems.push(mergedItem);
              processedUserIds.add(newItem.id);
            } else {
              // No user modification for this item - use new item as-is
              mergedItems.push(newItem);
            }
          }

          // Add remaining user items that don't have a matching new item
          // (user-created items that don't exist in new data)
          for (const userItem of userItems) {
            if (userItem.id && !processedUserIds.has(userItem.id)) {
              mergedItems.push(userItem);
            }
          }

          group.items = mergedItems;
          // Remove from map so we don't add duplicates
          preservedUserItems.delete(key);
        }
      }

      // Add preserved user groups that don't exist in new data
      // These are groups where ALL items were user-modified (user-created groups)
      const categoryPrefix = `${normalizedCatName}|`;
      for (const [key, userItems] of preservedUserItems.entries()) {
        if (key.startsWith(categoryPrefix)) {
          const groupName = key.slice(categoryPrefix.length);
          // Ensure groups array exists before pushing
          if (!newCategory.groups) {
            newCategory.groups = [];
          }
          // Create a new group with only the preserved user items
          newCategory.groups.push({
            name: groupName,
            items: userItems,
          });
          preservedUserItems.delete(key);
        }
      }

      targetResult.json.categories.push(newCategory);
    }
  }

  // Handle orphaned user groups - groups in categories that don't exist in new data
  // Example: user creates "AD" group in "steel-beams-joists" category, but API returns
  // different category names like "joist", "w_medium", etc.
  if (preservedUserItems.size > 0) {
    // Group remaining items by their original category
    const orphanedCategories = new Map<string, { groupName: string; items: any[] }[]>();

    for (const [key, userItems] of preservedUserItems.entries()) {
      const [normalizedCatName, groupName] = key.split("|");
      if (!orphanedCategories.has(normalizedCatName)) {
        orphanedCategories.set(normalizedCatName, []);
      }
      orphanedCategories.get(normalizedCatName)!.push({ groupName, items: userItems });
    }

    // Find a target result to add orphaned categories to
    const targetResult = mergedResults[0] || {
      pdf: "orphaned.pdf",
      json: { categories: [] },
    };
    if (!mergedResults.includes(targetResult)) {
      mergedResults.push(targetResult);
    }
    if (!targetResult.json.categories) {
      targetResult.json.categories = [];
    }

    // Add each orphaned category with its groups
    for (const [, groups] of orphanedCategories.entries()) {
      // Use the scope name as category name for orphaned items
      // This keeps them organized under the scope they belong to
      const categoryName = scope;

      // Check if this category already exists in the target (by scope name)
      let existingCategory = targetResult.json.categories.find(
        (c) => normalizeScopeName(c.name) === normalizeScopeName(categoryName)
      );

      if (!existingCategory) {
        // Create new category for orphaned groups
        existingCategory = {
          name: categoryName,
          trade: newData.trade as any,
          groups: [],
        };
        targetResult.json.categories.push(existingCategory);
      }

      // Add groups to the category
      for (const { groupName, items } of groups) {
        const existingGroup = existingCategory.groups.find((g) => g.name === groupName);
        if (existingGroup) {
          existingGroup.items.push(...items);
        } else {
          existingCategory.groups.push({ name: groupName, items });
        }
      }
    }

    preservedUserItems.clear();
  }

  // Remove empty results (no categories left)
  const filteredResults = mergedResults.filter(
    (r) => r.json?.categories && r.json.categories.length > 0,
  );

  // If all results are empty, return the new data as-is
  if (filteredResults.length === 0) {
    return newData;
  }

  return {
    ...existingData,
    result: filteredResults,
    takeoff_page_id: newData.takeoff_page_id,
  };
};

/**
 * Renumbers items in a group that have duplicate numbered names.
 * Each area-scoped takeoff starts numbering from 1, so merging produces
 * multiple "Wall Footing 1" items. This renumbers them sequentially.
 *
 * Detects two patterns:
 *   "Wall Footing 1"     → base "Wall Footing",  format: `${base} ${n}`
 *   "Concrete Slab (3)"  → base "Concrete Slab",  format: `${base} (${n})`
 */
const renumberDuplicateItemNames = (items: { name: string }[]): void => {
  // Group items by base name pattern
  const groups = new Map<string, { format: "space" | "paren"; indices: number[] }>();

  for (let i = 0; i < items.length; i++) {
    const name = items[i].name;

    // "Name 1" pattern
    const spaceMatch = name.match(/^(.+?)\s+(\d+)$/);
    if (spaceMatch) {
      const key = `s:${spaceMatch[1]}`;
      if (!groups.has(key)) groups.set(key, { format: "space", indices: [] });
      groups.get(key)!.indices.push(i);
      continue;
    }

    // "Name (1)" pattern
    const parenMatch = name.match(/^(.+?)\s*\((\d+)\)$/);
    if (parenMatch) {
      const key = `p:${parenMatch[1].trimEnd()}`;
      if (!groups.has(key)) groups.set(key, { format: "paren", indices: [] });
      groups.get(key)!.indices.push(i);
      continue;
    }
  }

  // Only renumber groups that have duplicate names
  for (const [key, group] of groups) {
    const names = group.indices.map((i) => items[i].name);
    if (new Set(names).size === names.length) continue; // all unique, skip

    const base = key.slice(2); // strip "s:" or "p:" prefix
    group.indices.forEach((itemIdx, seqIdx) => {
      items[itemIdx].name = group.format === "space"
        ? `${base} ${seqIdx + 1}`
        : `${base} (${seqIdx + 1})`;
    });
  }
};

/**
 * Merges new scope data into existing unified results without removing existing items.
 * Used for area-scoped takeoffs where each area produces additive results.
 *
 * Unlike replaceScopeInUnifiedResults which removes all existing items for the scope,
 * this function keeps existing items and adds new ones, de-duplicating by item ID.
 */
export const mergeScopeIntoUnifiedResults = (
  existingData: UnifiedMeasurementData,
  newData: UnifiedMeasurementData,
  // Kept for API symmetry with replaceScopeInUnifiedResults; not used because
  // additive merging matches categories by normalized name, not by scope.
  _scope: string,
): UnifiedMeasurementData => {
  const mergedResults: UnifiedResult[] = JSON.parse(JSON.stringify(existingData.result));

  for (const newResult of newData.result) {
    if (!newResult.json?.categories) continue;

    // Find a matching target result in existing data.
    let targetResult = mergedResults.find(
      (r) =>
        r.pdf === newResult.pdf ||
        r.output_path === newResult.output_path ||
        (r.pageId != null && r.pageId === newResult.pageId),
    );

    if (!targetResult) {
      targetResult = mergedResults.length > 0
        ? mergedResults[0]
        : { pdf: newResult.pdf, json: { categories: [] } };
      if (!mergedResults.includes(targetResult)) {
        mergedResults.push(targetResult);
      }
    }

    if (!targetResult.json.categories) {
      targetResult.json.categories = [];
    }

    for (const newCategory of newResult.json.categories) {
      const normalizedNewCatName = normalizeScopeName(newCategory.name);

      // Find existing category by normalized name
      const existingCategory = targetResult.json.categories.find(
        (c) => normalizeScopeName(c.name) === normalizedNewCatName,
      );

      if (!existingCategory) {
        // Category doesn't exist yet — add it wholesale
        targetResult.json.categories.push(JSON.parse(JSON.stringify(newCategory)));
        continue;
      }

      // Merge groups into existing category
      for (const newGroup of newCategory.groups || []) {
        const existingGroup = existingCategory.groups.find(
          (g) => g.name === newGroup.name,
        );

        if (!existingGroup) {
          // Group doesn't exist — add it wholesale
          existingCategory.groups.push(JSON.parse(JSON.stringify(newGroup)));
          continue;
        }

        // Merge items: add new items, skip duplicates.
        // Primary dedup by ID; for items without IDs, fall back to a
        // composite key (name|type|qty) so retries don't create duplicates.
        const existingIds = new Set(
          existingGroup.items
            .filter((item) => item.id)
            .map((item) => item.id),
        );
        const itemFingerprint = (item: { name: string; type?: string; qty?: number }) =>
          `${item.name}|${item.type ?? ""}|${item.qty ?? ""}`;
        const existingFingerprints = new Set(
          existingGroup.items
            .filter((item) => !item.id)
            .map(itemFingerprint),
        );
        let addedNew = false;
        for (const newItem of newGroup.items || []) {
          if (newItem.id) {
            if (!existingIds.has(newItem.id)) {
              existingGroup.items.push(JSON.parse(JSON.stringify(newItem)));
              addedNew = true;
            }
          } else {
            const fp = itemFingerprint(newItem);
            if (!existingFingerprints.has(fp)) {
              existingGroup.items.push(JSON.parse(JSON.stringify(newItem)));
              existingFingerprints.add(fp);
              addedNew = true;
            }
          }
        }

        // Renumber items with duplicate names (e.g., multiple "Wall Footing 1")
        if (addedNew) {
          renumberDuplicateItemNames(existingGroup.items);
        }
      }
    }
  }

  const filteredResults = mergedResults.filter(
    (r) => r.json?.categories && r.json.categories.length > 0,
  );

  if (filteredResults.length === 0) {
    return newData;
  }

  return {
    ...existingData,
    result: filteredResults,
    takeoff_page_id: newData.takeoff_page_id,
  };
};

/**
 * Maps scopes for "Skip Labels" mode by replacing "wall-types" with "walls"
 * while preserving all other selected scopes.
 *
 * "wall-types" requires a label bounding box for detection; "walls" does not.
 * When skipping labels, we use "walls" so the backend doesn't attempt
 * label-based detection without a bounding box.
 */
export const mapScopesForSkipLabels = (scopes: string[]): string[] =>
  scopes.map((scope) => (scope === "wall-types" ? "walls" : scope));

/**
 * Determines whether the takeoff polling loop should terminate.
 *
 * The agentic backend emits intermediate S3 links (e.g. tables/extract.json)
 * while the job is still "processing". The old inline check
 * (`loadedScopesCount >= expectedScopesLength`) would fire on those
 * intermediate links and stop polling before the real scope result arrived.
 *
 * Rules:
 * - Always stop on "failed", "completed", or "cancelled" (terminal states).
 * - Never stop based on scope count alone while status is "processing", "queued", or undefined.
 * - For any other unknown status, fall back to the scope count check.
 */
export const isTakeoffPollingComplete = (
  status: string | undefined,
  loadedScopesCount: number,
  expectedScopesLength: number
): boolean => {
  if (status === "failed" || status === "completed" || status === "cancelled") return true;
  if (!status || status === "processing" || status === "queued") return false;
  return loadedScopesCount >= expectedScopesLength;
};

/**
 * Some Takeoff Intelligence scopes require prerequisites from the extraction
 * sidebar. Example: `lighting-fixtures` needs an attached luminaire schedule
 * whose IS_LINEAR / FIXED_LENGTH columns the CP handler parses to classify
 * linear vs. point fixtures. Without an attached schedule, the handler falls
 * back to image-only detection which empirically produces 0 results.
 *
 * This helper is used by the Takeoff Intelligence UI to disable Run and show
 * an inline prompt when a user selects a prerequisite-gated scope but hasn't
 * attached the required artifact yet. Keep the logic here (not inside the
 * component) so it's unit-testable.
 */
export const scopeRequiresScheduleAttachment = (
  selectedScopes: string[],
  attachedScheduleCount: number,
): boolean => {
  if (attachedScheduleCount > 0) return false;
  return selectedScopes.some((scope) => SCOPES_REQUIRING_SCHEDULE.has(scope));
};

/**
 * Scopes whose takeoff quality depends on an extracted schedule. Single
 * source of truth for both the "attach a schedule" gate
 * (scopeRequiresScheduleAttachment) and the "schedules still extracting"
 * informational prompt (scopeNeedsSchedules, ENG-14985).
 *
 * TODO(ENG-14985): confirm the full list with product. Starting from the
 * verified lighting-fixtures case (luminaire schedule drives
 * linear-vs-point classification); expand as other schedule-dependent
 * scopes are confirmed. Tokens must match scopeOptions value ids exactly
 * (case-sensitive).
 */
export const SCOPES_REQUIRING_SCHEDULE = new Set(["lighting-fixtures"]);

/**
 * ENG-14985: does any selected scope depend on a schedule? Drives the
 * non-blocking "schedules for [scope] are still extracting" inline prompt
 * shown at trade/scope selection in Takeoff Intelligence (individual page)
 * and Bulk Takeoffs (all drawings). Pure + colocated so it's unit-testable
 * and shared by both entry points. Case-sensitive token match against
 * SCOPES_REQUIRING_SCHEDULE.
 */
export const scopeNeedsSchedules = (selectedScopes: string[]): boolean =>
  selectedScopes.some((scope) => SCOPES_REQUIRING_SCHEDULE.has(scope));

/**
 * ENG-14985: returns the scope id that actually TRIGGERS the notice — the first
 * selected scope requiring a schedule — so the notice names the right scope even
 * when the schedule-dependent scope isn't first in the selection (e.g.
 * ["receptacles-outlets", "lighting-fixtures"] must name Lighting Fixtures, not
 * Receptacles). Falls back to the first selected scope, then "". Single-owner so
 * both entry points derive the label identically.
 */
export const triggeringScheduleScope = (selectedScopes: string[]): string =>
  selectedScopes.find((scope) => SCOPES_REQUIRING_SCHEDULE.has(scope)) ??
  selectedScopes[0] ??
  "";

/**
 * Creates a React state updater that marks matching takeoff records as cancelled.
 * Used by PdfManager and TakeoffIntelligence stop handlers to prevent
 * pollers from overwriting cancelled status with stale data.
 */
export const markTakeoffRecordsAsCancelled = (
  takeoffIds: string[]
): ((prev: TakeoffRecord[]) => TakeoffRecord[]) => {
  const cancelledSet = new Set(takeoffIds);
  return (prev: TakeoffRecord[]) =>
    prev.map((record) => {
      const key = record.external_id ?? record.id;
      if (cancelledSet.has(key) || cancelledSet.has(record.id)) {
        return { ...record, status: "cancelled", queue: undefined };
      }
      return record;
    });
};

// The takeoff polling endpoint returns records for a single job_id at a time.
// A naive full-replacement would discard records from other jobs that
// loadPagesFromDatabase hydrated, hiding pages from earlier uploads in the
// TradePanel and thumbnail grid on multi-job projects.
export const mergePolledTakeoffRecords = (
  prev: TakeoffRecord[],
  polled: TakeoffRecord[],
  currentJobId: string,
): TakeoffRecord[] => {
  const otherJobRecords = prev.filter(
    (record) => record.job_id && record.job_id !== currentJobId,
  );
  return [...otherJobRecords, ...polled];
};

// Builds a grid card directly from a ProjectPage (the Pages-tab source of
// truth) for pages that never made it into the takeoffRecords-derived `files`
// list. See reconcileFilesWithProjectPages for why this is needed.
const fileItemFromProjectPage = (page: ProjectPage): PdfFileItem => ({
  id: page.external_id,
  pageId: page.id,
  filename: page.title || page.code || "Unknown",
  s3_url: page.pdf_s3_link || "",
  webp_url: page.webp_s3_link || null,
  pageCode: page.code,
  trade: page.trade || inferTradeFromPageCode(page.code),
  scale: page.scale,
  rotation: page.rotation,
  isArchived: page.is_archived,
  uploadBatchNumber: page.upload_batch_number,
  createdAt: page.created_at,
  sourcePdf: extractFilename(page.source_pdf),
  pageContentType: page.page_content_type,
});

// ENG-16570: the center takeoff grid renders `files`, which PdfManager derives
// by mapping over `takeoffRecords`. In-session cancel/poll paths can prune
// `takeoffRecords` (e.g. a stuck takeoff that was stopped drops to
// completed-only), while `projectPages` — the Pages-tab source — keeps every
// page. The result is the reported symptom: 49 pages in the Pages tab but only
// a few cards in the center grid, with the rest silently missing.
//
// `projectPages` is authoritative for which pages EXIST, so reconcile against
// it: keep the richer `files` entries as-is (they carry takeoff status,
// overrides, needs-attention, etc.) and append a card for every non-archived
// projectPage that isn't already represented. Archived pages are intentionally
// skipped — they belong in the Archive panel, not the grid. The grid can then
// never render fewer pages than the DB knows about.
export const reconcileFilesWithProjectPages = (
  files: PdfFileItem[],
  projectPages: Record<number, ProjectPage>,
): PdfFileItem[] => {
  // Index which pages `files` already covers, by both external_id and numeric
  // page id (pre-sync records key on the numeric Rails id, not the UUID).
  const representedExternalIds = new Set<string>();
  const representedPageIds = new Set<number>();
  for (const file of files) {
    representedExternalIds.add(String(file.id));
    if (file.pageId) representedPageIds.add(file.pageId);
  }

  const missing = Object.values(projectPages).filter(
    (page) =>
      !page.is_archived &&
      !representedPageIds.has(page.id) &&
      !representedExternalIds.has(page.external_id),
  );

  if (missing.length === 0) return files;

  return [...files, ...missing.map(fileItemFromProjectPage)];
};

// Narrows the AI takeoff-handler catalog to combos the org is also licensed for.
// `orgRestrictions == null` is the documented "no restriction" sentinel from
// /api/v1/allowed_trade_scopes — return the full handler list unchanged.
export const intersectAllowedTradeScopes = (
  aiHandlers: TakeoffHandler[],
  orgRestrictions: Array<{ trade: string; scope: string }> | null | undefined,
): TakeoffHandler[] => {
  if (!orgRestrictions) return aiHandlers;
  const orgSet = new Set(orgRestrictions.map((r) => `${r.trade}|${r.scope}`));
  return aiHandlers.filter((h) => orgSet.has(`${h.trade}|${h.scope}`));
};
